import React, { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { api } from '../../utils/api'
import '../../styles/dashboard.css'

function WarehouseHome() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const userName = user?.name || user?.email?.split('@')[0] || 'User'

  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadStats()
  }, [])

  async function loadStats() {
    try {
      const response = await api.get('/api/warehouse/stats')
      setStats(response.stats)
    } catch (error) {
      console.error('Failed to load stats:', error)
    } finally {
      setLoading(false)
    }
  }

  // Get greeting based on time of day
  function getGreeting() {
    const hour = new Date().getHours()
    if (hour < 12) return 'Good morning'
    if (hour < 17) return 'Good afternoon'
    return 'Good evening'
  }

  const flowStages = [
    {
      id: 'receiving',
      path: '/warehouse/receiving',
      label: 'Receiving',
      icon: '📦',
      color: '#3BB273',
      tone: 'mint',
      getValue: () => stats?.receiving?.waiting || 0,
      getSub: () => 'incoming'
    },
    {
      id: 'testing',
      path: '/warehouse/testing',
      label: 'Testing',
      icon: '🔧',
      color: '#FFD44D',
      tone: 'sand',
      getValue: () => stats?.testing?.waiting || 0,
      getSub: () => 'waiting'
    },
    {
      id: 'issue-trades',
      path: '/warehouse/issue-trades',
      label: 'Issue Trades',
      icon: '⚠️',
      color: '#E5625E',
      tone: 'rose',
      getValue: () => stats?.issueTrades?.pending || 0,
      getSub: () => 'pending'
    },
    {
      id: 'repairs',
      path: '/warehouse/repairs',
      label: 'Repairs',
      icon: '🛠️',
      color: '#8B5CF6',
      tone: 'peach',
      getValue: () => stats?.repairs?.inProgress || 0,
      getSub: () => 'in progress'
    },
    {
      id: 'photography',
      path: '/warehouse/photography',
      label: 'Photography',
      icon: '📷',
      color: '#EC4899',
      tone: 'stone',
      getValue: () => stats?.photography?.pending || 0,
      getSub: () => 'pending'
    },
    {
      id: 'shipping',
      path: '/warehouse/shipping',
      label: 'Shipping',
      icon: '🚚',
      color: '#0EA5E9',
      tone: 'stone',
      getValue: () => stats?.shipping?.pending || 0,
      getSub: () => 'pending',
      comingSoon: true
    }
  ]

  const quickActions = [
    { label: '+ New Receiving', path: '/warehouse/receiving', icon: '📥' },
    { label: 'Log Repair', path: '/warehouse/repairs', icon: '🔧' },
    { label: 'Issue Trades', path: '/warehouse/issue-trades', icon: '⚠️' },
    { label: 'Start Testing', path: '/warehouse/testing', icon: '🔍' },
    { label: 'Create Product', path: '/warehouse/manual-listing', icon: '➕' }
  ]

  // Calculate totals for status badges
  const totalReceiving = stats?.receiving?.waiting || 0
  const totalTesting = stats?.testing?.waiting || 0
  const totalIssueTrades = stats?.issueTrades?.pending || 0
  const totalRepairs = stats?.repairs?.inProgress || 0

  return (
    <div className="dashboard-page">
      {/* Header */}
      <div className="pageHeader">
        <h1 className="pageTitle">
          {getGreeting()}, <span className="pageTitle__name">{userName}</span>
        </h1>
        <p className="pageSub">
          Warehouse Status <span style={{ color: '#3BB273', fontWeight: 600 }}>Live</span>
        </p>
      </div>

      {/* Status Badges Row */}
      <div className="kpiGrid" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
        <Link to="/warehouse/receiving" className="kpi kpi--mint">
          <div className="kpi__top">
            <span className="kpi__title">Await Receiving</span>
            <span className="kpi__icon">📦</span>
          </div>
          <div className="kpi__value">{loading ? '...' : totalReceiving}</div>
          <div className="kpi__sub">incoming shipments</div>
        </Link>

        <Link to="/warehouse/testing" className="kpi kpi--sand">
          <div className="kpi__top">
            <span className="kpi__title">Await Testing</span>
            <span className="kpi__icon">🔧</span>
          </div>
          <div className="kpi__value">{loading ? '...' : totalTesting}</div>
          <div className="kpi__sub">items to test</div>
        </Link>

        <Link to="/warehouse/issue-trades" className="kpi kpi--rose">
          <div className="kpi__top">
            <span className="kpi__title">Issue Trades</span>
            <span className="kpi__icon">⚠️</span>
          </div>
          <div className="kpi__value">{loading ? '...' : totalIssueTrades}</div>
          <div className="kpi__sub">need attention</div>
        </Link>

        <Link to="/warehouse/repairs" className="kpi kpi--peach">
          <div className="kpi__top">
            <span className="kpi__title">Repairs</span>
            <span className="kpi__icon">🛠️</span>
          </div>
          <div className="kpi__value">{loading ? '...' : totalRepairs}</div>
          <div className="kpi__sub">in progress</div>
        </Link>
      </div>

      {/* Primary Action Cards */}
      <div className="panelGrid panelGrid--even" style={{ marginTop: 24 }}>
        <div className="panel">
          <div className="panel__head">
            <div>
              <h3 className="panel__title">Start Receiving</h3>
              <p className="panel__sub">Mark incoming shipments as received</p>
            </div>
          </div>
          <div className="taskList">
            <Link to="/warehouse/receiving" className="taskRow taskRow--active">
              <div className="taskRow__left">
                <span className="taskRow__icon">📦</span>
                <span className="taskRow__label">Open Receiving</span>
              </div>
              <span className="pill pill--onDark">{totalReceiving} waiting</span>
              <span className="taskRow__chev">›</span>
            </Link>
          </div>
        </div>

        <div className="panel">
          <div className="panel__head">
            <div>
              <h3 className="panel__title">Test Items</h3>
              <p className="panel__sub">Inspect and grade received products</p>
            </div>
          </div>
          <div className="taskList">
            <Link to="/warehouse/testing" className="taskRow taskRow--active" style={{
              background: 'linear-gradient(90deg, rgba(255,212,77,.95), rgba(255,212,77,.88))',
              borderColor: 'rgba(255,212,77,.55)'
            }}>
              <div className="taskRow__left">
                <span className="taskRow__icon">🔧</span>
                <span className="taskRow__label">Open Testing</span>
              </div>
              <span className="pill pill--onDark">{totalTesting} waiting</span>
              <span className="taskRow__chev">›</span>
            </Link>
          </div>
        </div>
      </div>

      {/* Flow Overview */}
      <div className="panel" style={{ marginTop: 24 }}>
        <div className="panel__head">
          <div>
            <h3 className="panel__title">Flow Overview</h3>
            <p className="panel__sub">Items progressing through warehouse stages</p>
          </div>
          <button className="iconBtn--ghost" onClick={loadStats} title="Refresh">
            ↻
          </button>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: 12,
          marginTop: 12
        }}>
          {flowStages.map(stage => (
            <Link
              key={stage.id}
              to={stage.comingSoon ? '#' : stage.path}
              onClick={e => stage.comingSoon && e.preventDefault()}
              className={`kpi kpi--${stage.tone}`}
              style={{
                minHeight: 100,
                opacity: stage.comingSoon ? 0.5 : 1,
                cursor: stage.comingSoon ? 'not-allowed' : 'pointer'
              }}
            >
              <div className="kpi__top">
                <span className="kpi__title">{stage.label}</span>
                <span className="kpi__icon">{stage.icon}</span>
              </div>
              <div className="kpi__value" style={{ fontSize: 28 }}>
                {loading ? '...' : stage.getValue()}
              </div>
              <div className="kpi__sub">
                {stage.comingSoon ? 'Coming soon' : stage.getSub()}
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="panel" style={{ marginTop: 24 }}>
        <div className="panel__head">
          <div>
            <h3 className="panel__title">Quick Actions</h3>
            <p className="panel__sub">Common warehouse tasks</p>
          </div>
        </div>

        <div style={{
          display: 'flex',
          gap: 10,
          flexWrap: 'wrap',
          marginTop: 12
        }}>
          {quickActions.map((action, i) => (
            <Link
              key={i}
              to={action.path}
              className="taskRow"
              style={{ flex: '1 1 auto', minWidth: 140 }}
            >
              <div className="taskRow__left">
                <span className="taskRow__icon">{action.icon}</span>
                <span className="taskRow__label">{action.label}</span>
              </div>
              <span className="taskRow__chev">›</span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  )
}

export default WarehouseHome
